import * as React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from 'src/shared/helpers/utils';

export type AlertSeverity = 'info' | 'warning' | 'error' | 'success';

export type AlertProps = React.HTMLAttributes<HTMLDivElement> & {
    severity?: AlertSeverity;
    icon?: React.ReactNode;
    variant?: 'soft' | 'filled';
};

const defaultSeverityIcons: Record<AlertSeverity, React.ReactNode> = {
    info: <Info className='size-[18px] shrink-0' aria-hidden />,
    warning: <AlertTriangle className='size-[18px] shrink-0' aria-hidden />,
    error: <AlertCircle className='size-[18px] shrink-0' aria-hidden />,
    success: <CheckCircle2 className='size-[18px] shrink-0' aria-hidden />,
};

const severityToClasses: Record<AlertSeverity, { soft: string; filled: string }> = {
    info: {
        soft: 'border border-blue-20 bg-blue-0 text-blue-100',
        filled: 'bg-blue-100 text-white',
    },
    warning: {
        soft: 'border border-orange-20 bg-orange-0 text-orange-100',
        filled: 'bg-orange-100 text-white',
    },
    error: {
        soft: 'border border-red-20 bg-red-0 text-red-100',
        filled: 'bg-red-100 text-white',
    },
    success: {
        soft: 'border border-green-20 bg-green-0 text-green-100',
        filled: 'bg-green-100 text-white',
    },
};

export default function Alert({ severity = 'info', icon, className, children, variant = 'soft', ...props }: AlertProps) {
    const variantClasses = severityToClasses[severity][variant];
    const resolvedIcon = icon !== undefined ? icon : defaultSeverityIcons[severity];

    return (
        <div role='alert' className={cn('w-full rounded-md p-3 text-sm flex items-start gap-2', variantClasses, className)} {...props}>
            {resolvedIcon ? <span className='mt-0.5 shrink-0'>{resolvedIcon}</span> : null}
            <div className='flex-1'>{children}</div>
        </div>
    );
}
